import { useMemo, useState } from "react";

import {
  type BannerAspect,
  type CampaignType,
  type IBannerGalleryItem,
} from "@/@types/gamification";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useGetBannerGalleryQuery } from "@/services/gamification";

import {
  BANNER_GALLERY_SEARCH_DEBOUNCE_MS,
  GALLERY_ALL_GAMES,
  GallerySourceFilter,
} from "../constants/banner-gallery";

export type GalleryGameFilter = CampaignType | typeof GALLERY_ALL_GAMES;

interface UseBannerGalleryArgs {
  aspect: BannerAspect;
  initialCampaignType?: CampaignType;
}

export interface UseBannerGalleryResult {
  items: IBannerGalleryItem[];
  isLoading: boolean;
  isError: boolean;
  retry: () => void;
  sourceFilter: GallerySourceFilter;
  setSourceFilter: (value: GallerySourceFilter) => void;
  gameFilter: GalleryGameFilter;
  setGameFilter: (value: GalleryGameFilter) => void;
  search: string;
  setSearch: (value: string) => void;
  showAllAspects: boolean;
  setShowAllAspects: (value: boolean) => void;
}

function matchesSource(item: IBannerGalleryItem, filter: GallerySourceFilter): boolean {
  return filter === GallerySourceFilter.ALL || item.source === filter;
}

export function useBannerGallery({
  aspect,
  initialCampaignType,
}: UseBannerGalleryArgs): UseBannerGalleryResult {
  const [sourceFilter, setSourceFilter] = useState<GallerySourceFilter>(
    GallerySourceFilter.ALL,
  );
  const [gameFilter, setGameFilter] = useState<GalleryGameFilter>(
    initialCampaignType ?? GALLERY_ALL_GAMES,
  );
  const [search, setSearch] = useState<string>("");
  const [showAllAspects, setShowAllAspects] = useState<boolean>(false);
  const debouncedSearch: string = useDebouncedValue(
    search.trim(),
    BANNER_GALLERY_SEARCH_DEBOUNCE_MS,
  );

  const { data, isFetching, isError, refetch } = useGetBannerGalleryQuery({
    campaignType: gameFilter === GALLERY_ALL_GAMES ? undefined : gameFilter,
    search: debouncedSearch || undefined,
  });

  const items: IBannerGalleryItem[] = useMemo(
    () =>
      (data ?? []).filter(
        (item) =>
          matchesSource(item, sourceFilter) &&
          (showAllAspects || item.aspect === aspect),
      ),
    [data, sourceFilter, showAllAspects, aspect],
  );

  return {
    items,
    isLoading: isFetching,
    isError,
    retry: refetch,
    sourceFilter,
    setSourceFilter,
    gameFilter,
    setGameFilter,
    search,
    setSearch,
    showAllAspects,
    setShowAllAspects,
  };
}
